import React, { useCallback, useEffect, useState } from "react";
import { Dialog, Notification } from "@rio-cloud/rio-uikit";
import Button from "@rio-cloud/rio-uikit/Button";
import { DICT, formatChecklistTitle, formatMaskTime } from "@utils";
import { CeckpointStepV2 } from "src/types/checkpoint";
import { createNewCheckpoint, getTemplateCheckpointV2, uploadStepDocument } from "src/services/checkpoint";
import { FileEntry, StepRender } from "./StepRender";
import ConfirmDialog from "../Dialogs/Confirm";
import { time2minutes, minutesToHHMM } from "src/utils/time2minutes";

interface Props {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  stopType: string;
}

const CreateCheckpoint = ({ open, onClose, stopType }: Props) => {
  const [checkpointType, setCheckpointType] = useState<string>(stopType);
  const [checkpointId, setCheckpointId] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [steps, setSteps] = useState<CeckpointStepV2[]>([]);
  const [stepFiles, setStepFiles] = useState<Record<string, FileEntry[]>>({});
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [estimatedTimeMinutes, setEstimatedTimeMinutes] = useState("00:00");
  const [quantity, setQuantity] = useState(0);
  const checklistTitle = formatChecklistTitle(checkpointType);

  const init = useCallback(async () => {
    setTitle("");
    setEstimatedTimeMinutes("00:00");
    setQuantity(0);
    setSteps([]);
    setStepFiles({});
    setCheckpointType(stopType);

    const template = await getTemplateCheckpointV2(stopType);
    if (template.data.result) {
      setSteps(template.data.result);
    }
  }, [stopType]);

  useEffect(() => {
    const totalMinutes = steps.reduce((total, step) => {
      return total + time2minutes(step.estimatedTimeMinutes ?? "00:00");
    }, 0);

    const totalTime = minutesToHHMM(totalMinutes);
    setEstimatedTimeMinutes(totalTime);
  }, [steps]);

  useEffect(() => {
    if (open) {
      init();
    }
  }, [open, init]);

  const handleDeleteStep = (i: number) => {
    setSteps((prevSteps) => prevSteps.filter((_, index) => index !== i));
  };

  const handleChangeCheckpointType = (checkpointType: string) => {
    setCheckpointId(checkpointType);
    setShowConfirmDialog(true);
  };

  const handleConfirmChangeCheckpointType = async () => {
    const template = await getTemplateCheckpointV2(checkpointId);

    setCheckpointType(checkpointId);
    if (template.data.result) {
      setSteps(template.data.result);
    } else {
      setSteps([]);
    }
    setShowConfirmDialog(false);
  };

  const addStep = async () => {
    const id = checkpointId !== "" ? checkpointId : checkpointType;

    const template = await getTemplateCheckpointV2(id);
    if (template.data.result) {
      setSteps((prevSteps) => [...prevSteps, ...template.data.result]);
    }
  };

  const buildFieldPayload = (field: any) => {
    const payload: any = {
      required: field.required ?? false,
      estimatedValue: field.estimatedValue === null || field.estimatedValue === undefined
        ? null
        : String(field.estimatedValue),
    };

    if (field.id && field.id !== "") {
      payload.id = field.id;
    }

    if (field.field) {
      payload.field = field.field;
    }

    if (field.optionValue !== undefined) {
      payload.optionValue = field.optionValue;
    }

    return payload;
  };

  const handleUploadFiles = async (checkpointId: string, savedSteps: any[]) => {
    const hasFiles = Object.values(stepFiles).some((files) => files.length > 0);
    if (!hasFiles) return;

    for (let i = 0; i < savedSteps.length; i++) {
      const files = stepFiles[String(i)];
      if (!files || files.length === 0) continue;

      const step = savedSteps[i];
      const docField = step.fields?.find((f: any) => f.field?.name === "DOCUMENT" || f.field?.id === 19);
      if (!docField?.id) continue;

      for (const entry of files) {
        try {
          await uploadStepDocument(checkpointId, step.id, docField.id, entry.file);
        } catch (error) {
          console.error(`Erro ao enviar anexo "${entry.file.name}":`, error);
          Notification.error(`Erro ao enviar anexo "${entry.file.name}"`);
        }
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    const payload: any = {
      checkpointType,
      title,
      estimatedTimeMinutes: time2minutes(estimatedTimeMinutes),
      quantity,
      steps: steps.map(({ estimatedTimeMinutes, ...step }) => ({
        ...step,
        estimatedTimeMinutes: time2minutes(estimatedTimeMinutes ?? ""),
        fields: step.fields?.map((field) => buildFieldPayload(field)),
      })),
    };

    try {
      const checkpoint = await createNewCheckpoint(payload);

      if (checkpoint.status !== "success") {
        Notification.error(`Erro ao criar ${checklistTitle.toLowerCase()}`);
        setSaving(false);
        return;
      }

      const created = checkpoint.data?.result;

      if (created?.id && created?.steps) {
        await handleUploadFiles(created.id, created.steps);
      }

      Notification.success(`${checklistTitle} criado com sucesso.`);
      onClose(false);
    } catch (error) {
      console.error("Erro ao criar checklist:", error);
      Notification.error(`Erro ao criar ${checklistTitle.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        show={open}
        onClose={() => onClose(false)}
        onEsc={() => onClose(false)}
        bsSize="xl"
        className="checkpointDialog"
        bodyClassName="padding-25"
        title={`Criar ${checklistTitle}`}
        body={
          <form>
            <div className="lead">
              <div className="text-size-h6 text-bold">Dados do checklist</div>

              <div className="display-flex flex-row gap-10">
                <div className="flex-1-1 form-group">
                  <label form="checkpoint_type">Tipo de parada</label>
                  <select
                    disabled
                    form="checkpoint_type"
                    className="form-control"
                    id="select"
                    defaultValue={DICT.REFUELING}
                    value={checkpointType}
                    onChange={(e) => handleChangeCheckpointType(e.target.value)}
                  >
                    {Object.entries(DICT).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1-1 form-group">
                  <label form="title">Título do checklist</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    placeholder="Título do checklist"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="flex-1-1 form-group">
                  <label form="time">Tempo estimado do checklist</label>
                  <input
                    disabled
                    id="time"
                    type="text"
                    className="form-control"
                    min="00:00"
                    // max="23:59"
                    value={estimatedTimeMinutes}
                    placeholder="00:00"
                  />
                </div>
                {checkpointType !== "MECHANICAL_EMERGENCY" && (
                  <div className="flex-1-1 form-group">
                    <label form="quantity">Quantidade (Unidades)</label>
                    <input
                      type="number"
                      className="form-control"
                      id="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </div>

            <hr />
            {steps.length > 0 ? (
              <StepRender steps={steps} deleteStep={handleDeleteStep} updateStep={setSteps} onFilesChange={setStepFiles} checkpointType={checkpointType} />
            ) : (
              <></>
            )}
            <Button iconName="rioglyph rioglyph-plus" onClick={addStep}>
              Adicionar Etapa
            </Button>
            <hr />
            <div className="display-flex justify-content-end">
              <Button className="btn btn-primary" disabled={saving} onClick={async () => await handleSubmit()}>
                {saving ? "Salvando..." : "Salvar Checklist"}
              </Button>
            </div>
          </form>
        }
      ></Dialog>
      <ConfirmDialog
        show={showConfirmDialog}
        onClickConfirm={async () => await handleConfirmChangeCheckpointType()}
        onClickCancel={() => {
          setShowConfirmDialog(false);
          setCheckpointId("");
        }}
        title="Alterar Tipo de Checklist"
        content="Você tem certeza que deseja alterar o tipo de checklist? Isso pode afetar as etapas associadas."
        confirmButtonText="Sim, alterar"
        cancelButtonText="Não, manter"
      />
    </>
  );
};

export default CreateCheckpoint;
